import { FaBookmark, FaShieldHeart } from "react-icons/fa6";
import {
  FaDumbbell,
  FaGifts,
  FaInfinity,
  FaLockOpen,
  FaTag,
} from "react-icons/fa";

import { Button } from "@heroui/button";
import {
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/modal";
import { cn } from "@heroui/theme";
import { ReactNode, useContext, useState } from "react";
import { Card, Chip, Spinner } from "@heroui/react";
import { PrimaryButton } from "../PrimaryButton";
import { useFetcher } from "@remix-run/react";
import { AppContext } from "~/context/AppContext";
import posthog from "posthog-js";

export const PaywallModalContent = () => {
  const REFILL_STORE = [
    {
      hearts: 5,
      price: 2.99,
    },
    {
      hearts: 10,
      price: 4.99,
    },
    {
      hearts: 20,
      price: 10.99,
      discount: 0.3,
    },
  ];
  const features = [
    {
      icon: <FaInfinity />,
      label: "Unlimited Hearts",
    },
    { icon: <FaLockOpen />, label: "Unlock All Scenarios" },
    { icon: <FaGifts />, label: "Special Badges" },
    { icon: <FaDumbbell />, label: "Practice Skills" },
    { icon: <FaBookmark />, label: "Bookmark Responses" },
    { icon: <FaShieldHeart />, label: "Cancel anytime" },
  ];
  const [quickRefill, setQuickRefill] = useState<number | null>(null);
  const [isSubscription, setIsSubscription] = useState(true);
  const { gameData } = useContext(AppContext);
  const fetcher = useFetcher();

  return (
    <ModalContent>
      {(onClose) => (
        <>
          <ModalHeader className="flex flex-col gap-1 font-bangers text-xl text-center">
            Out of Hearts?
          </ModalHeader>

          <ModalBody className="flex flex-col gap-2 text-sm  font-semibold text-center">
            <p className="opacity-80">
              You&apos;ve used up your hearts. That means you&apos;re actually
              putting in the reps.
            </p>

            <div className="flex flex-col gap-2 bg-zinc-900 p-4 rounded-xl">
              <p className="text-sm font-bold text-zinc-300 uppercase">
                Quick Refill
              </p>
              <div className="flex justify-center gap-4">
                {REFILL_STORE.map((item) => (
                  <Card
                    isPressable
                    key={item.hearts}
                    className={cn(
                      "flex flex-col justify-center items-center gap-1 bg-gradient-to-r from-zinc-800 to-zinc-700 rounded-lg py-2 px-4 text-sm font-bold text-white relative",
                      quickRefill === item.hearts
                        ? "opacity-100 border-2"
                        : "opacity-50"
                    )}
                    onPress={() => {
                      setQuickRefill(item.hearts);
                      setIsSubscription(false);
                    }}
                  >
                    <p>{item.hearts} Hearts</p>
                    <p>${item.price.toFixed(2)}</p>
                  </Card>
                ))}
              </div>
            </div>

            <div className="relative flex items-center justify-center my-1">
              <hr className="w-full border-zinc-700" />
              <span className="px-3 text-zinc-500 uppercase z-10">or</span>
              <hr className="w-full border-zinc-700" />
            </div>

            <Card
              isPressable
              onPress={() => {
                setIsSubscription(true);
                setQuickRefill(null);
              }}
              classNames={{
                base: "min-h-[360px]",
              }}
              className={cn(
                "flex flex-col gap-2 bg-gradient-to-r from-zinc-900 to-zinc-800 p-4 rounded-xl",
                isSubscription && "opacity-100 border-2 border-primary"
              )}
            >
              <p className="text-sm font-bold text-zinc-300 uppercase">
                ManUp PRO
              </p>

              <div className="text-xs opacity-80">
                Becoming the man women chase — and other men respect.
              </div>

              <div className="grid grid-cols-3 gap-2 mt-2">
                {features.map((feature, index) => (
                  <FeatureItem
                    key={index}
                    icon={feature.icon}
                    label={feature.label}
                  />
                ))}
              </div>

              <p className="text-lg font-bangers text-white">
                $5.99/week
                {gameData?.couponPercentOff && (
                  <div>
                    <Chip
                      color="success"
                      startContent={<FaTag />}
                      variant="faded"
                      size="sm"
                      className="font-bangers text-xs"
                    >
                      {gameData.couponPercentOff}% off at checkout
                    </Chip>
                  </div>
                )}
              </p>
            </Card>
          </ModalBody>

          <ModalFooter className="flex flex-col justify-center items-center gap-1">
            <PrimaryButton
              disabled={
                (!quickRefill && !isSubscription) ||
                fetcher.state === "submitting" ||
                fetcher.state === "loading"
              }
              onClick={async () => {
                posthog.capture(
                  `purchase_initiated_${
                    isSubscription ? "subscription" : `refill-${quickRefill}`
                  }`
                );
                // handle purchase
                fetcher.submit(
                  {
                    product: isSubscription
                      ? "subscription"
                      : `refill-${quickRefill}`,
                    redirectUrl: window.location.href,
                  },
                  {
                    method: "POST",
                    action: "/api/create-checkout-session",
                    encType: "multipart/form-data",
                  }
                );
              }}
            >
              {fetcher.state === "submitting" || fetcher.state === "loading" ? (
                <Spinner variant="wave" color="white" />
              ) : (
                <>{isSubscription ? "Go Pro" : "Refill Hearts"}</>
              )}
            </PrimaryButton>

            <Button
              color="danger"
              variant="light"
              onPress={onClose}
              className="w-full font-bangers text-sm"
            >
              Maybe Later
            </Button>
          </ModalFooter>
        </>
      )}
    </ModalContent>
  );
};

type FeatureItemProps = {
  icon: ReactNode;
  label: string;
};

export const FeatureItem = ({ icon, label }: FeatureItemProps) => (
  <div className="flex flex-col items-center justify-between border-2 rounded-xl border-neutral-600 p-2 gap-2">
    <div className="text-2xl">{icon}</div>
    <p className="text-xs font-mono text-zinc-400 text-center mt-1 leading-tighter">
      {label}
    </p>
  </div>
);
